import React, { useState, useEffect } from 'react';
import { Box, Container, Card, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, CircularProgress, Typography, Dialog, TextField, DialogTitle, DialogContent, DialogActions, Snackbar, Alert, Grid, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { userService } from '../services/api';
import { Add, Edit, Search, TrendingUp, VerifiedUser, Schedule } from '@mui/icons-material';

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [toast, setToast] = useState({ open: false, message: '', type: 'success' });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipcode: '',
    pan_number: ''
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('name-asc');
  const [pageNumber, setPageNumber] = useState(1);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  });

  // Fetch users whenever search, sort, or page changes
  useEffect(() => {
    fetchUsers();
  }, [searchQuery, sortBy, pageNumber]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await userService.getAllUsers({
        page: pageNumber,
        limit: 10,
        search: searchQuery,
        sort: sortBy
      });
      
      let data = [];
      let paginationData = {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 10
      };

      if (response.data?.data && Array.isArray(response.data.data)) {
        data = response.data.data;
        paginationData = response.data.pagination || paginationData;
      } else if (Array.isArray(response.data)) {
        data = response.data;
      } else if (response.data?.success && response.data?.data) {
        data = response.data.data;
        paginationData = response.data.pagination || paginationData;
      }
      
      setUsers(data);
      setPagination(paginationData);
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]);
      setToast({ open: true, message: 'Error loading users', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = () => {
    setEditingUser(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      zipcode: '',
      pan_number: ''
    });
    setOpenDialog(true);
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name || '',
      email: user.email || '',
      phone: user.phone || '',
      address: user.address || '',
      city: user.city || '',
      state: user.state || '',
      zipcode: user.zipcode || '',
      pan_number: user.pan_number || ''
    });
    setOpenDialog(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    try {
      if (!formData.name || !formData.email) {
        setToast({ open: true, message: 'Name and Email are required', type: 'error' });
        return;
      }

      if (editingUser) {
        await userService.updateUser(editingUser.id, formData);
      } else {
        await userService.createUser(formData);
      }

      setToast({
        open: true,
        message: editingUser ? 'User updated successfully!' : 'User created successfully!',
        type: 'success'
      });

      setOpenDialog(false);
      await fetchUsers();
    } catch (error) {
      console.error('Error saving user:', error);
      setToast({
        open: true,
        message: 'Error saving user: ' + (error.response?.data?.error || error.message),
        type: 'error'
      });
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ backgroundColor: '#f5f7fa', minHeight: '100%', p: 4 }}>
      <Container maxWidth={false} sx={{ maxWidth: '100%' }}>
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1a202c', mb: 1 }}>
                Users Management
              </Typography>
              <Typography variant="body2" sx={{ color: '#667085' }}>
                Manage user accounts and information
              </Typography>
            </Box>
            <Button 
              variant="contained" 
              startIcon={<Add />}
              onClick={handleAddUser}
              sx={{ backgroundColor: '#2c5f2d', '&:hover': { backgroundColor: '#1f4620' } }}
            >
              Add New User
            </Button>
          </Box>
        </Box>

        {/* Stats Summary */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 3, mb: 4 }}>
          {/* Total Users Card */}
          <Card sx={{ 
            p: 3,
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafb 100%)',
            border: '1px solid #e8ecf1',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              boxShadow: '0 8px 16px rgba(0,0,0,0.12)',
              transform: 'translateY(-2px)'
            }
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ backgroundColor: '#dcfce7', p: 2, borderRadius: '12px' }}>
                <TrendingUp sx={{ color: '#2c5f2d', fontSize: 28 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ color: '#667085', fontWeight: '500' }}>
                  Total Users
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#2c5f2d', mt: 0.5 }}>
                  {users.length}
                </Typography>
                <Typography variant="caption" sx={{ color: '#999', display: 'block', mt: 0.5 }}>
                  Active accounts
                </Typography>
              </Box>
            </Box>
          </Card>

          {/* City Wise Card */}
          <Card sx={{ 
            p: 3,
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafb 100%)',
            border: '1px solid #e8ecf1',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              boxShadow: '0 8px 16px rgba(0,0,0,0.12)',
              transform: 'translateY(-2px)'
            }
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ backgroundColor: '#e0e7ff', p: 2, borderRadius: '12px' }}>
                <VerifiedUser sx={{ color: '#4f46e5', fontSize: 28 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ color: '#667085', fontWeight: '500' }}>
                  Users with PAN
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#4f46e5', mt: 0.5 }}>
                  {users.filter(u => u.pan_number).length}
                </Typography>
                <Typography variant="caption" sx={{ color: '#999', display: 'block', mt: 0.5 }}>
                  Verified donors
                </Typography>
              </Box>
            </Box>
          </Card>

          {/* Recent Users Card */}
          <Card sx={{ 
            p: 3,
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafb 100%)',
            border: '1px solid #e8ecf1',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              boxShadow: '0 8px 16px rgba(0,0,0,0.12)',
              transform: 'translateY(-2px)'
            }
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ backgroundColor: '#fef3c7', p: 2, borderRadius: '12px' }}>
                <Schedule sx={{ color: '#d97706', fontSize: 28 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ color: '#667085', fontWeight: '500' }}>
                  Users with City
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#d97706', mt: 0.5 }}>
                  {users.filter(u => u.city).length}
                </Typography>
                <Typography variant="caption" sx={{ color: '#999', display: 'block', mt: 0.5 }}>
                  Location updated
                </Typography>
              </Box>
            </Box>
          </Card>
        </Box>

        {/* Search and Filter Section */}
        <Card sx={{ p: 3, mb: 3, border: '1px solid #e8ecf1', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={6}>
              <TextField
                fullWidth
                placeholder="Search by name, email, city, phone..."
                variant="outlined"
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: '#667085' }} />,
                }}
                sx={{
                  backgroundColor: '#fff',
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: '#e8ecf1' }
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  label="Sort By"
                  onChange={(e) => {
                    setSortBy(e.target.value);
                    setPageNumber(1);
                  }}
                  sx={{ backgroundColor: '#fff' }}
                >
                  <MenuItem value="name-asc">Name (A-Z)</MenuItem>
                  <MenuItem value="name-desc">Name (Z-A)</MenuItem>
                  <MenuItem value="email-asc">Email (A-Z)</MenuItem>
                  <MenuItem value="email-desc">Email (Z-A)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Card>

        <Card sx={{ border: '1px solid #e8ecf1', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
          <TableContainer>
            <Table>
              <TableHead sx={{ backgroundColor: '#f8fafb', borderBottom: '2px solid #e8ecf1' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: '600', color: '#1a202c', py: 2 }}>ID</TableCell>
                  <TableCell sx={{ fontWeight: '600', color: '#1a202c' }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: '600', color: '#1a202c' }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: '600', color: '#1a202c' }}>Phone</TableCell>
                  <TableCell sx={{ fontWeight: '600', color: '#1a202c' }}>City</TableCell>
                  <TableCell sx={{ fontWeight: '600', color: '#1a202c', textAlign: 'center' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users && users.length > 0 ? (
                  users.map(user => (
                    <TableRow 
                      key={user.id}
                      sx={{ '&:hover': { backgroundColor: '#f8fafb' }, borderBottom: '1px solid #e8ecf1' }}
                    >
                    <TableCell sx={{ color: '#667085' }}>{user.id}</TableCell>
                    <TableCell sx={{ fontWeight: '500', color: '#1a202c' }}>{user.name}</TableCell>
                    <TableCell sx={{ color: '#667085' }}>{user.email}</TableCell>
                    <TableCell sx={{ color: '#667085' }}>{user.phone}</TableCell>
                    <TableCell sx={{ color: '#667085' }}>{user.city}</TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      <Button 
                        size="small"
                        startIcon={<Edit />}
                        onClick={() => handleEditUser(user)}
                        sx={{ color: '#2c5f2d', mr: 1 }}
                      >
                        Edit
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4, color: '#667085' }}>
                      No users found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination Footer */}
          <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f9fafb', borderTop: '1px solid #e8ecf1' }}>
            <Typography variant="body2" sx={{ color: '#667085' }}>
              Showing {users.length > 0 ? (pageNumber - 1) * pagination.itemsPerPage + 1 : 0} to {Math.min(pageNumber * pagination.itemsPerPage, pagination.totalItems)} of {pagination.totalItems} users
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button 
                variant="outlined" 
                size="small" 
                disabled={pageNumber === 1}
                onClick={() => setPageNumber(pageNumber - 1)}
              >
                Previous
              </Button>
              {[...Array(pagination.totalPages)].map((_, i) => (
                <Button
                  key={i + 1}
                  variant={pageNumber === i + 1 ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => setPageNumber(i + 1)}
                  sx={{
                    minWidth: '36px',
                    backgroundColor: pageNumber === i + 1 ? '#059669' : 'transparent',
                    color: pageNumber === i + 1 ? '#fff' : '#667085',
                    borderColor: '#d5d9e2',
                    '&:hover': {
                      backgroundColor: pageNumber === i + 1 ? '#059669' : '#f3f4f6',
                      borderColor: '#667085'
                    }
                  }}
                >
                  {i + 1}
                </Button>
              ))}
              <Button 
                variant="outlined" 
                size="small"
                disabled={pageNumber === pagination.totalPages || pagination.totalPages === 0}
                onClick={() => setPageNumber(pageNumber + 1)}
              >
                Next
              </Button>
            </Box>
          </Box>
        </Card>

        {/* Dialog for adding/editing user */}
        <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontWeight: '600', color: '#1a202c' }}>
            {editingUser ? 'Edit User' : 'Add New User'}
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <TextField
              fullWidth
              label="Name *"
              name="name"
              value={formData.name}
              onChange={handleFormChange}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Email *"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleFormChange}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Phone"
              name="phone"
              value={formData.phone}
              onChange={handleFormChange}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Address"
              name="address"
              value={formData.address}
              onChange={handleFormChange}
              margin="normal"
            />
            <TextField
              fullWidth
              label="City"
              name="city"
              value={formData.city}
              onChange={handleFormChange}
              margin="normal"
            />
            <TextField
              fullWidth
              label="State"
              name="state"
              value={formData.state}
              onChange={handleFormChange}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Zipcode"
              name="zipcode"
              value={formData.zipcode}
              onChange={handleFormChange}
              margin="normal"
            />
            <TextField
              fullWidth
              label="PAN Number"
              name="pan_number"
              value={formData.pan_number}
              onChange={handleFormChange}
              margin="normal"
            />
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} variant="contained" sx={{ backgroundColor: '#2c5f2d' }}>
              {editingUser ? 'Update User' : 'Create User'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Toast Notification */}
        <Snackbar
          open={toast.open}
          autoHideDuration={6000}
          onClose={() => setToast({ ...toast, open: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setToast({ ...toast, open: false })}
            severity={toast.type}
            sx={{ width: '100%' }}
          >
            {toast.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default UsersPage;
